import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { API_URL, QUOTATION_PUBLIC_URL, QUOTATION_PUBLIC_ACCEPT_URL } from "../../constants/api";
import { Button } from "../../components/ui/button";
import QuotationDocument from "./QuotationDocument";

export default function QuotationPublic() {
  const { token } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [selected, setSelected] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted]   = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}${QUOTATION_PUBLIC_URL(token)}`)
      .then(r => {
        if (r.data?.status === "success") {
          setData(r.data);
          const accPlanId = r.data.quotation?.accepted_plan_id;
          if (accPlanId) setAccepted(accPlanId);
        } else setError(r.data?.message || "Failed");
      })
      .catch(e => setError(e.response?.data?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    if (!selected) return;
    setAccepting(true);
    try {
      const r = await axios.post(`${API_URL}${QUOTATION_PUBLIC_ACCEPT_URL(token)}`, { plan_id: selected });
      if (r.data?.status === "success") {
        setAccepted(selected);
      } else {
        alert(r.data?.message || "Failed");
      }
    } catch (e) {
      alert(e.response?.data?.message || "Failed");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return <div style={Center}>Loading quotation…</div>;
  if (error) return <div style={{ ...Center, color: "#b91c1c" }}><AlertCircle style={{ marginRight: 8 }} /> {error}</div>;

  const { quotation: q, template: t } = data;

  return (
    <div style={{ background: "#f1f5f9", minHeight: "100vh", paddingBottom: 24 }}>
      {/* Sticky action bar */}
      <div style={Bar}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, color: "#0f172a" }}>{q.quote_number}</h3>
          <p style={{ margin: 0, fontSize: 12, color: "#64748b" }}>Prepared for {q.customer_name}</p>
        </div>
        {accepted ? (
          <div style={Accepted}>
            <CheckCircle2 style={{ marginRight: 6 }} />
            Accepted — {q.plans.find(p => p.id === accepted)?.plan_label}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#475569" }}>Select a plan to accept:</span>
            <select value={selected || ""} onChange={e => setSelected(Number(e.target.value))}
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid #cbd5e1", borderRadius: 6 }}>
              <option value="">— choose —</option>
              {q.plans.map(p => (
                <option key={p.id} value={p.id}>{p.plan_label} — ₹{Number(p.grand_total).toLocaleString("en-IN")}</option>
              ))}
            </select>
            <Button size="sm" onClick={accept} disabled={!selected || accepting}>
              {accepting ? "Accepting..." : "Accept Plan"}
            </Button>
          </div>
        )}
      </div>

      <QuotationDocument quote={q} template={t} modules={[]} />
    </div>
  );
}

const Center = { minHeight: "60vh", display: "grid", placeItems: "center", color: "#475569", fontFamily: "system-ui" };
const Bar = {
  position: "sticky", top: 0, zIndex: 10, background: "#fff",
  borderBottom: "1px solid #e5e7eb",
  padding: "10px 24px", display: "flex", justifyContent: "space-between", alignItems: "center",
};
const Accepted = {
  background: "#dcfce7", color: "#166534", padding: "6px 12px",
  borderRadius: 6, display: "flex", alignItems: "center", fontWeight: 600, fontSize: 13,
};
